/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../../ts';
import { Collection } from '../../../../ts/collection';
import { RootState } from '../../../../ts/state';
import { isDefaultAction } from '../../util/actionUtil';

export const collectionsAdapter = createEntityAdapter<Collection>({
  selectId: collection => collection.collection
});
const defaultSelectors = collectionsAdapter.getSelectors<RootState>(
  state => state.collections
);
export const selectCollectionsCount = defaultSelectors.selectTotal;
export const selectAllCollections = defaultSelectors.selectAll;
export const selectCollectionById = defaultSelectors.selectById;
export const selectCurrentCollectionId = (state: RootState) =>
  state.collections?.currentCollectionId;
export const selectCurrentCollection = (state: RootState) =>
  selectCollectionById(state, selectCurrentCollectionId(state));
export const selectIsListLoading = (state: RootState) => {
  return (
    state.collections?.listStatus === ApiStatus.LOADING ||
    !state.actions?.actionsLoaded
  );
};
export const selectIsCurrentCollectionLoading = (state: RootState) =>
  state.collections?.currentLoadingStatus === ApiStatus.LOADING;
export const selectListError = (state: RootState) =>
  state.collections?.listError;
export const selectCurrentError = (state: RootState) =>
  state.collections?.currentError;
export const selectActions = (state: RootState) => state.collections?.actions;
export const selectCustomActions = (state: RootState) =>
  state.collections?.actions.filter(
    action => !isDefaultAction(action.action) && !action.newAction
  );
export const getCollectionsActions = createSelector(
  selectActions,
  selectAllCollections,
  selectListError,
  selectIsListLoading,
  (actions, collections, listError, loading) => ({
    actions,
    list: collections,
    listError,
    listLoading: loading
  })
);
export const getCollections = createSelector(
  selectAllCollections,
  selectListError,
  selectIsListLoading,
  (collections, listError, loading) => ({
    list: collections,
    listError,
    listLoading: loading
  })
);
export const getCollection = createSelector(
  selectCurrentCollection,
  selectCurrentError,
  selectIsCurrentCollectionLoading,
  (collection, currentError, loading) => ({
    current: collection,
    currentError,
    currentLoading: loading
  })
);
export const getAllAvailableFilters = (state: RootState) =>
  state.collections?.availableFilters;
export const getSelectedFilters = (state: RootState) =>
  state.collections?.selectedFilters;
export const areFiltersApplied = (state: RootState) =>
  state.collections?.filtersApplied;

export const getFilteredData = (state: RootState) =>
  state.collections?.filteredData;

export const getFilteredWord = (state: RootState) =>
  state?.collections?.filteredWord;

//gets all actions including the ones within collections.
export const getAllActions = createSelector(
  getCollectionsActions,
  collectionsActions => {
    const allActions = [];
    collectionsActions?.actions?.map(action => {
      if (!isDefaultAction(action?.action)) {
        const customAction = {
          ...action,
          id: action?.action,
          examples:
            action?.intents?.length > 0 ? action.intents[0].examples_count : 0,
          counts: action?.counts?.steps || action?.variables?.length
        };
        allActions.push(customAction);
      }
    });
    collectionsActions?.list?.map(collectionList => {
      collectionList.action_references.map(action => {
        const updatedAction = {
          ...action,
          id: action?.action,
          examples:
            action?.intents?.length > 0 ? action.intents[0].examples_count : 0,
          counts: action?.counts?.steps || action?.variables?.length
        };
        allActions.push(updatedAction);
      });
    });
    return allActions;
  }
);
